import { SyncStatus } from "~/graphql/supergraphEnums";

/**
 * Shared derivation for the descriptive GL sync status (EQ-4892), used by:
 *  - the entities list "Last JSQ sync" column, per transaction-category row (EQ-4962)
 *  - the transfer batch overview (per-batch label)
 *
 * `describeBatchSyncStatus` maps a single sync status to its descriptive phase
 * (Consolidating / Loading / Completed / Failed) plus whether to spin.
 */

// The status fields come from generated query types whose `SyncStatus` enum is a
// distinct (nominal) declaration from the one in `supergraphEnums`. Both are string
// enums sharing the same values, so we accept a plain `string` and compare against
// the `SyncStatus` constants by value — matching how the entities list already treats
// these fields (`string | null`).
export type SubSyncStatus = string | null | undefined;

export enum GlSyncPhase {
  /** `started` — backend is consolidating GL transactions. */
  Consolidating = "consolidating",
  /** `published` / `received` — consolidated data is loading into JSQ. */
  Loading = "loading",
  /** `completed` (or `null` + a prior `last_sync_at`). */
  Completed = "completed",
  /** Any sub-sync (or the entity) reported `failed`. */
  Failed = "failed",
  /** Nothing in flight and nothing ever synced — render nothing. */
  Idle = "idle",
}

/** Statuses that mean a sub-sync is still actively working. */
const isInFlight = (status: SubSyncStatus): boolean =>
  status === SyncStatus.Started ||
  status === SyncStatus.Published ||
  status === SyncStatus.Received ||
  status === SyncStatus.MarkedForSync ||
  status === SyncStatus.SyncInProgress;

/**
 * How far a single sub-sync has advanced:
 *   started / marked / in-progress (0) < published / received (1) < completed / null (2).
 * `failed` is handled before this is ever called.
 */
const rankOf = (status: SubSyncStatus): number => {
  switch (status) {
    case SyncStatus.Started:
    case SyncStatus.MarkedForSync:
    case SyncStatus.SyncInProgress:
      return 0;
    case SyncStatus.Published:
    case SyncStatus.Received:
      return 1;
    // `completed`, `null`/`undefined`, and any settings-level status: treated as done.
    default:
      return 2;
  }
};

const CONSOLIDATING = "Consolidating GL transactions";
const LOADING = "Loading consolidated transactions into JSQ";

export type GlBatchDisplay = {
  phase: GlSyncPhase;
  label: string;
  showSpinner: boolean;
};

/**
 * Per-batch descriptive status for the transfer batch overview. Each row is a single
 * batch, so there is no X/N count. Returns `null` for non-sync states (ready, idle,
 * settings-level) so the caller keeps its existing display.
 */
export const describeBatchSyncStatus = (status: SubSyncStatus): GlBatchDisplay | null => {
  if (status === SyncStatus.Failed) {
    return { phase: GlSyncPhase.Failed, label: "Failed", showSpinner: false };
  }
  if (status === SyncStatus.Completed) {
    return { phase: GlSyncPhase.Completed, label: "Completed", showSpinner: false };
  }
  if (isInFlight(status) && rankOf(status) === 0) {
    return { phase: GlSyncPhase.Consolidating, label: CONSOLIDATING, showSpinner: true };
  }
  if (status === SyncStatus.Published || status === SyncStatus.Received) {
    return { phase: GlSyncPhase.Loading, label: LOADING, showSpinner: true };
  }
  return null;
};
